import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceSelectorProxy} from 'relay-runtime'

import type {
  ApplyPendingIssueTypeSuggestionInput,
  applyPendingIssueTypeSuggestionMutation,
  applyPendingIssueTypeSuggestionMutation$data,
} from './__generated__/applyPendingIssueTypeSuggestionMutation.graphql'

// Must match the args used by the `recentTriageEvents` field in
// `useRecentAgentActivity_issue` so we can look up the same store record.
const RECENT_TRIAGE_ITEM_TYPES = [
  'LABELED_EVENT',
  'UNLABELED_EVENT',
  'ISSUE_TYPE_ADDED_EVENT',
  'ISSUE_TYPE_CHANGED_EVENT',
  'ISSUE_TYPE_REMOVED_EVENT',
  'ISSUE_FIELD_CHANGED_EVENT',
  'ISSUE_FIELD_ADDED_EVENT',
  'ISSUE_FIELD_REMOVED_EVENT',
]

export type ApplyOptimisticEvent = {
  actorTypename: string
  actorLogin: string
  actorAvatarUrl: string
  actorDisplayName: string | null
  issueTypeName: string
  issueTypeColor: string | null
  rationale: string | null
}

export function commitApplyPendingIssueTypeSuggestionMutation({
  environment,
  input,
  since,
  optimisticEvent,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: ApplyPendingIssueTypeSuggestionInput
  since: string
  optimisticEvent: ApplyOptimisticEvent
  onError?: (error: Error) => void
  onCompleted?: (response: applyPendingIssueTypeSuggestionMutation$data) => void
}) {
  return commitMutation<applyPendingIssueTypeSuggestionMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation applyPendingIssueTypeSuggestionMutation($input: ApplyPendingIssueTypeSuggestionInput!) {
        applyPendingIssueTypeSuggestion(input: $input) {
          clientMutationId
        }
      }
    `,
    variables: {input},
    optimisticUpdater: store => applyOptimisticUpdate(store, input, since, optimisticEvent),
    updater: store => applyOptimisticUpdate(store, input, since, optimisticEvent),
    onError: error => onError?.(error),
    onCompleted: response => onCompleted?.(response),
  })
}

function applyOptimisticUpdate(
  store: RecordSourceSelectorProxy,
  input: ApplyPendingIssueTypeSuggestionInput,
  since: string,
  event: ApplyOptimisticEvent,
) {
  const issue = store.get(input.issueId)
  if (!issue) return

  const now = new Date().toISOString()
  const uniqueSuffix = `${input.actorId}:${input.issueTypeId}:${Date.now()}`

  // 1. Mark the matching pending suggestion as APPLIED so the row hides.
  const suggestions = issue.getLinkedRecords('pendingTypeSuggestions')
  if (suggestions) {
    for (const s of suggestions) {
      const actor = s.getLinkedRecord('actor')
      const issueType = s.getLinkedRecord('issueType')
      if (actor?.getValue('id') === input.actorId && issueType?.getValue('id') === input.issueTypeId) {
        s.setValue('APPLIED', 'state')
      }
    }
  }

  // 2. Build a fake IssueTypeAddedEvent record (with actor + issueType).
  const actorRecordId = `client:optimistic-actor:${input.actorId}:${Date.now()}`
  const actorRecord = store.create(actorRecordId, event.actorTypename)
  actorRecord.setValue(event.actorTypename, '__typename')
  actorRecord.setValue(event.actorLogin, 'login')
  actorRecord.setValue(event.actorAvatarUrl, 'avatarUrl')
  if (event.actorDisplayName) {
    actorRecord.setValue(event.actorDisplayName, 'displayName')
  }

  const issueTypeRecordId = `client:optimistic-issueType:${input.issueTypeId}:${Date.now()}`
  const issueTypeRecord = store.create(issueTypeRecordId, 'IssueType')
  issueTypeRecord.setValue(event.issueTypeName, 'name')
  if (event.issueTypeColor) {
    issueTypeRecord.setValue(event.issueTypeColor, 'color')
  }

  const eventId = `client:optimistic-event:${uniqueSuffix}`
  const eventRecord = store.create(eventId, 'IssueTypeAddedEvent')
  eventRecord.setValue('IssueTypeAddedEvent', '__typename')
  eventRecord.setValue(eventId, 'id')
  eventRecord.setValue(now, 'createdAt')
  eventRecord.setLinkedRecord(issueTypeRecord, 'issueType')
  eventRecord.setLinkedRecord(actorRecord, 'actor')

  // 3. Append it to the recentTriageEvents (timelineItems) connection-less list.
  const timelineArgs = {
    last: 25,
    since,
    visibleEventsOnly: true,
    itemTypes: RECENT_TRIAGE_ITEM_TYPES,
  }
  const timeline = issue.getLinkedRecord('timelineItems', timelineArgs)
  if (timeline) {
    const nodes = timeline.getLinkedRecords('nodes') ?? []
    timeline.setLinkedRecords([...nodes, eventRecord], 'nodes')
  }

  // 4. Append a matching IssueEventRationale so the action row gets a rationale.
  const rationaleRecordId = `client:optimistic-rationale:${uniqueSuffix}`
  const rationaleRecord = store.create(rationaleRecordId, 'IssueEventRationale')
  rationaleRecord.setValue(event.rationale ?? '', 'rationale')
  rationaleRecord.setLinkedRecord(eventRecord, 'issueEvent')

  const rationales = issue.getLinkedRecords('eventRationales') ?? []
  issue.setLinkedRecords([...rationales, rationaleRecord], 'eventRationales')
}
